import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { Send, Trash2, Pencil, Loader2, Check, X } from "lucide-react";

/**
 * Timeline notes tab used inside the Estimate / Invoice detail drawer.
 *
 * - Shows the legacy freeform `doc.notes` at the top (kept for backward compat
 *   with the note field on the create/edit form).
 * - Shows a newest-first list of `doc.noteEntries` timeline entries. Each entry
 *   has author + relative timestamp + edit/delete affordances for the author
 *   (and admins).
 * - Compose textarea at the bottom → posts to the parent doc AND mirrors the
 *   note onto the linked customer file (server-side).
 *
 * Props:
 *   doc: the estimate or invoice
 *   kind: "estimate" | "invoice" — used only for testids / accent color
 *   api: object with { addNote(id,text), updateNote(id,noteId,text), removeNote(id,noteId) }
 *   onUpdated(updated): called with the fresh server doc after any change
 *   currentUserEmail: email of the logged-in user (so we can gate edit/delete)
 *   isAdmin: bool — admins can edit/delete anyone's note
 */
export default function DocNotesTab({ doc, kind = "estimate", api, onUpdated, currentUserEmail, isAdmin = false }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  const entries = useMemo(() => {
    const arr = [...(doc?.noteEntries || [])];
    // Newest first — sort by createdAt desc.
    arr.sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
    return arr;
  }, [doc]);

  const accent = kind === "estimate" ? "blue" : "emerald";
  const accentBtn = kind === "estimate"
    ? "bg-blue-600 hover:bg-blue-700"
    : "bg-emerald-600 hover:bg-emerald-700";

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = text.trim();
    if (!trimmed) {
      toast.error("Type a note first.");
      return;
    }
    setBusy(true);
    try {
      const updated = await api.addNote(doc.id, trimmed);
      toast.success("Note added to " + kind + " and mirrored to customer file");
      setText("");
      onUpdated?.(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the note");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditingText(entry.text);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };
  const saveEdit = async (entry) => {
    const trimmed = editingText.trim();
    if (!trimmed) {
      toast.error("Note cannot be blank");
      return;
    }
    if (trimmed === entry.text) {
      cancelEdit();
      return;
    }
    setBusy(true);
    try {
      const updated = await api.updateNote(doc.id, entry.id, trimmed);
      toast.success("Note updated");
      cancelEdit();
      onUpdated?.(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update the note");
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async (entry) => {
    if (!window.confirm("Delete this note? It will be removed from the customer file too.")) return;
    setBusy(true);
    try {
      const updated = await api.removeNote(doc.id, entry.id);
      toast.success("Note removed");
      onUpdated?.(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove the note");
    } finally {
      setBusy(false);
    }
  };

  const canEdit = (entry) => isAdmin || entry.createdBy === currentUserEmail;

  return (
    <div className="space-y-4" data-testid={`doc-notes-tab-${kind}`}>
      {/* Legacy free-form notes field, if present — read-only summary. */}
      {doc?.notes && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
            Description
          </div>
          <div className="text-[13.5px] text-slate-700 whitespace-pre-wrap leading-relaxed">
            {doc.notes}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="space-y-2">
        <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
          Timeline notes ({entries.length})
        </div>
        {entries.length === 0 && (
          <div className="text-[13px] text-slate-400 italic px-1 py-2">
            No notes yet — add the first one below.
          </div>
        )}
        {entries.map((entry) => {
          const isMine = canEdit(entry);
          const isEditing = editingId === entry.id;
          const when = formatWhen(entry.createdAt);
          const edited = entry.updatedAt && entry.updatedAt !== entry.createdAt;
          return (
            <div
              key={entry.id}
              data-testid={`doc-note-entry-${entry.id}`}
              className={`rounded-lg border p-3 ${isMine ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50/60"}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="min-w-0 flex items-center gap-2 text-[11.5px]">
                  <span
                    className={`h-6 w-6 rounded-full text-white text-[10px] font-semibold flex items-center justify-center bg-${accent}-500 shrink-0`}
                    style={{ background: kind === "estimate" ? "#3b82f6" : "#10b981" }}
                  >
                    {(entry.createdBy || "?").slice(0, 2).toUpperCase()}
                  </span>
                  <span className="font-semibold text-slate-700 truncate">{entry.createdBy}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-500" title={entry.createdAt}>{when}</span>
                  {edited && <span className="text-slate-400 text-[10.5px] italic">(edited)</span>}
                </div>
                {isMine && !isEditing && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => startEdit(entry)}
                      disabled={busy}
                      data-testid={`doc-note-edit-${entry.id}`}
                      title="Edit"
                      className="h-7 w-7 rounded hover:bg-slate-100 text-slate-500 flex items-center justify-center"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => removeEntry(entry)}
                      disabled={busy}
                      data-testid={`doc-note-delete-${entry.id}`}
                      title="Delete"
                      className="h-7 w-7 rounded hover:bg-rose-50 text-rose-500 flex items-center justify-center"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
              {isEditing ? (
                <div className="space-y-2">
                  <textarea
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    autoFocus
                    rows={3}
                    className="w-full rounded-md border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none px-3 py-2 text-[13.5px] text-slate-800 bg-white"
                    data-testid={`doc-note-edit-input-${entry.id}`}
                  />
                  <div className="flex items-center gap-2 justify-end">
                    <button
                      onClick={cancelEdit}
                      disabled={busy}
                      className="h-8 px-3 rounded-md border border-slate-300 text-[12px] font-semibold text-slate-600 hover:bg-slate-50 flex items-center gap-1"
                    >
                      <X className="h-3.5 w-3.5" /> Cancel
                    </button>
                    <button
                      onClick={() => saveEdit(entry)}
                      disabled={busy}
                      data-testid={`doc-note-save-${entry.id}`}
                      className={`h-8 px-3 rounded-md text-white text-[12px] font-semibold flex items-center gap-1 ${accentBtn}`}
                    >
                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-[13.5px] text-slate-800 whitespace-pre-wrap leading-relaxed">
                  {entry.text}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Compose */}
      <form onSubmit={submit} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid={`doc-note-form-${kind}`}>
        <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
          Add a note
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`Add a note about this ${kind}... It will also be saved to the customer file.`}
          rows={3}
          disabled={busy}
          className="w-full rounded-md border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none px-3 py-2 text-[13.5px] text-slate-800 bg-white placeholder:text-slate-400"
          data-testid={`doc-note-input-${kind}`}
        />
        <div className="flex items-center justify-between">
          <div className="text-[11px] text-slate-500">
            Mirrored to the customer&apos;s file automatically.
          </div>
          <button
            type="submit"
            disabled={busy || !text.trim()}
            data-testid={`doc-note-submit-${kind}`}
            className={`h-9 px-4 rounded-md text-white text-[12.5px] font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${accentBtn}`}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Add note
          </button>
        </div>
      </form>
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = Date.now();
  const diffSec = Math.round((now - d.getTime()) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  if (diffSec < 604800) return `${Math.round(diffSec / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
